import React, { useEffect, useState } from "react";
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Input,
  Row,
  Spinner,
} from "reactstrap";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import { useTranslation } from "react-i18next";
import { getAuthUser } from "../../../helpers/utils";
import { useDispatch, useSelector } from "react-redux";
import CommonTable from "../../../Components/Common/Table/CommonTable";
import {
  activateDeactivateRoles,
  createRole,
  deleteRole,
  fetchAllRoles,
  fetchRoleTableColumnNames,
  fetchTranslation,
  updateRoles,
  updateUserPreference,
} from "../../../slices/thunks";
import DeleteModal from "../../../Components/Common/DeleteModal";
import ColumnSelectionModal from "../../../Components/Common/ColumnSelectionModal/ColumnSelectionModal";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import CommonAddModal from "../../../Components/Common/Modal/CommonAddModal";
import AddCommonForm from "../../../Components/Common/AddCommonForm/AddCommonForm";
import CommonDataTable from "../../../Components/Common/Table/CommonDataTable";
import usePermissions from "../../../Components/Hooks/usePermissions";
import addRolesmanagementForm from "../../../Components/Common/addRolesmanagementForm/addRolesmanagementForm";

const labels = [
  {
    id: 1,
    labelName: "RoleNameEnglish",
    fieldName: "text-box-translate",
    name: "RoleNameEn",
    value: "RoleNameEn",
    required: true,
    errorMessage: "Role Name English Is Required",
    minLength: 2,
    minLengthErrorMessage: "Role Name English must be at least 2 characters",
    maxLength: 20,
    maxLengthErrorMessage: "Role Name English must be at most 20 characters",
    translateTo: "RoleNameAr", 
  },
  {
    id: 2,
    labelName: "RoleNameArabic",
    fieldName: "text-box", 
    name: "RoleNameAr",
    value: "RoleNameAr",
    required: true,
    errorMessage: "Role Name Arabic Is Required",
    minLength: 2,
    minLengthErrorMessage: "Role Name Arabic must be at least 2 characters",
    maxLength: 20,
    maxLengthErrorMessage: "Role Name Arabic must be at most 20 characters"
  },
];

const actionConfig = {
  edit: {
    icon: "ri-edit-2-line",
    tooltip: "Edit",
  },
  delete: {
    icon: "ri-delete-bin-2-line",
    tooltip: "Delete",
  },
  permission: {
    icon: "ri-lock-unlock-line",
    tooltip: "Permission",
    // isVisible: (item) => item.Id != 2,
  },
};

  

function Rolesmanagement() {

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const {user} = getAuthUser();

  const [show, setShow] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [isAddOrEdit, setIsAddOrEdit] = useState("isAdd");
  const [selectedRow, setSelectedRow] = useState();
  const [selectedIds, setSelectedIds] = useState([]);
  const [deleteModal, setDeleteModal] = useState(false);
  const [role, setRole] = useState();
  const [modalOpen, setModalOpen] = useState(false);  
  const [selectedColumns, setSelectedColumns] = useState([]);
  const [lastTranslatedField, setLastTranslatedField] = useState(null); 

  const { roles, loading, error, tableColumnNames, Translations, translationLoading } = useSelector(
    (state) => ({
      roles: state.Roles.roles,
      tableColumnNames: state.Roles.tableColumnNames,
      loading: state.Roles.loading,
      error: state.Roles.error,
      Translations: state.Module.translations,
      translationLoading: state.Module.translationLoading, 
    })
  );

  // Compute permission
  const permission = (() => {
    // moduleId: 1 (Administration), pageId: 2 (Roles Management)
    return usePermissions(1, 1);
  })();



  // Navigate to Role Permissions
  const navigateToRolePermissions = (roleId) => {
    navigate("/administration/roles-management/page-permission", {
      state: { RoleId: roleId },
    });
  };

  
  const onAddClicksHandler = (value) => {
    setShow(true);
    setIsAddOrEdit(value);
    setSelectedRow({});
  };

  const onEditClickHandler = (role, value) => {
    setSelectedRow(role);
    setRole(role);
    setIsAddOrEdit(value);
    setShow(true);
  };  

  const onActiveOrDeactiveChange = (role, e) => {

    if (role.ID == user.RoleID) {
      toast.error("You cannot change the permission of this role");
      return;
    }

    dispatch(activateDeactivateRoles({ ID: role.ID }));
  };

  const confirmDelete = () => {
    
    if (selectedIds.includes(String(user.RoleID))) {
      toast.error("You cannot change the permission of this role");
      return;
    }

    dispatch(deleteRole({ IDs: selectedIds }))
      .then(() => {
        setSelectedIds([]);
      })
      .catch((error) => {
        console.error("Error deleting:", error);
      });
    setDeleteModal(false);
  };

  const onSubmitRoleHandler = (values, { resetForm }) => {

    // Base object with common properties
    const roleObj = {
      RoleNameEn: values.RoleNameEn.trim() || "",
      RoleNameAr: values.RoleNameAr.trim() || "",
    };

    // If editing, include RoleId and IsActive
    if (isAddOrEdit === "isEdit") {

      if (role && role.ID) {
        roleObj.ID = role.ID;
        roleObj.IsActive = role.IsActive;
      } 
    }

    if (isAddOrEdit === "isAdd") {
      dispatch(createRole(roleObj));
    } else {
      dispatch(updateRoles(roleObj));
    }

    resetForm();
    setShow(false);    
  };

  const onTranslateClickHandler = (targetField, textToTranslate, formikSetFieldValue, sourceFieldName) => {
    if (!textToTranslate || textToTranslate.trim() === "") {
      toast.error(t("EnterTextToTranslate"));
      return;
    }
    setLastTranslatedField(sourceFieldName); 

    dispatch(fetchTranslation({ LangText: textToTranslate }))
      .unwrap() 
      .then(translatedText => {
        formikSetFieldValue(targetField, translatedText);
      })
      .catch(error => {
        console.error("Translation error:", error);
        toast.error(t("TranslationFailed"));
      })
      .finally(() => {
        setLastTranslatedField(null); 
      });
  };


  const saveUserPreferences = async () => {
    const preferencesPayload = {
      ColumnIDs: tableColumnNames.reduce((acc, column) => {
        acc[column.ID] = selectedColumns.includes(column.ID);
        return acc;
      }, {}),
    };

    setModalOpen(false);
    try {
      await dispatch(updateUserPreference(preferencesPayload)).unwrap();
      await dispatch(
        fetchRoleTableColumnNames({
          module: "ROLES",
        })
      );

    } catch (error) {
      console.error("Error updating preferences:", error);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        await dispatch(
          fetchRoleTableColumnNames({
            module: "ROLES",
          })
        ).unwrap();
        await dispatch(
          fetchAllRoles()
        );
      } catch (error) {
        console.error(error);
      }
    };

    fetchData();
  }, [dispatch]);

  return (
    <div className="page-content">
      <Container fluid>
        <Row className="mb-3">
          <Col>
            <BreadCrumb title={t("AddRole")} />
          </Col>
        </Row>
        <div className="table-responsive">
          <Card>
            <CardHeader>
              <Row className="g-4">
                    <Col className="col-sm-auto">
                      <div>
                        <Button
                          className="btn btn-soft-danger ms-2 me-2"
                          onClick={() => setDeleteModal(true)}
                          disabled={selectedIds.length === 0}
                        >
                          <i className="ri-delete-bin-2-line"></i>
                        </Button>
                        {permission.CanAdd && (
                          <Button
                            color="secondary"
                            className="add-btn me-1"
                            onClick={() => onAddClicksHandler("isAdd")}
                            id="create-btn"
                            disabled={loading}
                          >
                            <i className="ri-add-line align-bottom me-1 ms-1"></i>
                            {t("AddNew")}
                          </Button>
                        )}
                      </div>
                    </Col>
                    <Col className="col-sm">
                      <div className="d-flex justify-content-sm-end">
                        <Button
                          className="btn btn-soft"
                          onClick={() => setModalOpen(true)}
                          disabled={loading}
                        >
                          <i className="ri-filter-3-line"></i>
                        </Button>
                        <div className="search-box ms-2 me-2">
                          <Input
                            type="text"
                            className="form-control search"
                            placeholder={t("Search")}
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                          />
                          <i className="ri-search-line search-icon"></i>
                        </div>
                      </div>
                    </Col>
              </Row>
            </CardHeader>

            <CardBody>
              <div className="listjs-table" id="customerList">
              
                {loading ? (
                  <div className="d-flex justify-content-center align-items-center">
                    <Spinner
                      style={{ height: "3rem", width: "3rem" }}
                      className="me-2"
                    >
                      Loading...
                    </Spinner>
                  </div>
                ) : (
                  <div className="listjs-table" id="customerList">
                    <CommonDataTable
                      data={roles}
                      tableColumnNames={tableColumnNames}
                      searchTerm={searchTerm}
                      onEditClickHandler={onEditClickHandler}
                      onActiveOrDeactiveChange={onActiveOrDeactiveChange}
                      setDeleteModal={setDeleteModal}
                      setSelectedIds={setSelectedIds}
                      handleNavigate={navigateToRolePermissions}
                      actionConfig={actionConfig}
                      permission={permission}
                    />
                  </div>
                )}
              </div>
            </CardBody>
          </Card>
        </div>
      </Container>

      {/* Modal  */}
       <CommonAddModal
        show={show}
        setShow={setShow}
        onSubmitHandler={onSubmitRoleHandler}
        labels={labels}
        isAddOrEdit={isAddOrEdit}
        selectedItemData={selectedRow}
        FormBody={addRolesmanagementForm}
        FormBodyProps={{ onTranslateClickHandler, translationLoading, lastTranslatedField }} 
        title={isAddOrEdit === "isAdd" ? t("AddNewRole") : t("EditNewRole")}
        size="lg"
      />
        

      {/* ColumnSelectionModal */}
      <ColumnSelectionModal
        isOpen={modalOpen}
        toggle={() => setModalOpen(!modalOpen)}
        columns={tableColumnNames}
        selectedColumns={selectedColumns}
        setSelectedColumns={setSelectedColumns}
        handleSave={saveUserPreferences}
      />

      {/* Delete modal  */}
      <DeleteModal
        showDelete={deleteModal}
        setShowDelete={setDeleteModal}
        onDeleteClick={confirmDelete}
        setSelectedIds={setSelectedIds}
        description={t("confirmDelete")}
      />
    </div>
  );
}

export default Rolesmanagement;
